// src/modules/wiring/utils/floorTransition.js
//
// Kat-geçiş oku (floor transition) — paylaşımlı mantık. Daha önce useComponentWiring
// içinde kablo bacasına kilitliydi; buraya TAŞINDI (extract) ve genelleştirildi:
// artık herhangi bir wire çizilirken (kablo bacası olmadan da) üst/alt kata gidiş
// oku konabilir. Tüm wire tipleri useComponentWiring'den geçtiği için bu modül oradan
// kullanılır; ileride cross-floor + devam mantığı da buraya taşınacak.
//
// Ok sembolü kablo bacasıyla AYNI (createArrow). Rotasyon konvansiyonu (0=sağ):
//   ArrowUp=60 (üst kat), ArrowDown=240 (alt kat), ArrowLeft=180, ArrowRight=0.

import { createArrow, createIncomingArrow } from '../../../factory/symbols/createArrows';
import { findFloorPlanMeshById, worldToMeshLocal } from '../../fpSync';
import { projectPointAcrossFloorMeshes } from '../../../utils/columnDetection';

export const FLOOR_ARROW_ROTATION = {
  ArrowUp: 60,
  ArrowDown: 240,
  ArrowLeft: 180,
  ArrowRight: 0,
};

export function directionFromRotation(rotation) {
  if (rotation === 60) return 'up';
  if (rotation === 240) return 'down';
  if (rotation === 180) return 'left';
  return 'right';
}

function disposeArrow(group) {
  if (!group) return;
  group.traverse((c) => {
    c.geometry?.dispose?.();
    if (Array.isArray(c.material)) c.material.forEach((m) => m?.dispose?.());
    else c.material?.dispose?.();
  });
}

/**
 * Preview ok'u oluştur/güncelle. arrowRefs: { previewRef, activeRef, rotationRef } (useRef'ler).
 * Mevcut preview varsa kaldırır, yeni yön/konumda oluşturur.
 */
export function updateFloorArrowPreview({ scene, position, rotation, wireColor, arrowRefs }) {
  if (!scene || !position) return null;
  if (arrowRefs.previewRef.current) {
    scene.remove(arrowRefs.previewRef.current);
    disposeArrow(arrowRefs.previewRef.current);
    arrowRefs.previewRef.current = null;
  }
  const group = createArrow(scene, {
    position: { x: position.x, y: position.y },
    rotation,
    color: wireColor || 0x0045ff,
    layerName: '0',
    isPreview: true,
  });
  arrowRefs.previewRef.current = group;
  arrowRefs.activeRef.current = true;
  arrowRefs.rotationRef.current = rotation;
  return group;
}

/**
 * Cross-floor işaretçi: kaynak kattaki ok'un HEDEF kattaki izdüşüm noktasına
 * "gelen ok" (incomingFloorTransitionArrow) koyar. Kullanıcı oradan hattı devam
 * ettirebilecek (Adım 3c). İzdüşüm kablo bacasıyla AYNI yöntemle bulunur
 * (projectPointAcrossFloorMeshes): en yakın kolon/merdiven referans alınıp hedef
 * kattaki karşılığı bulunur → çizimin mesh içindeki ötelenmesi telafi edilir.
 * Kolon/merdiven yoksa pür fpMesh-local izdüşüme düşülür.
 *
 * @returns {{marker, targetFloorId}|null}
 */
export function placeCrossFloorMarker({ scene, sortedFloors, sourceFloorPlanId, direction, worldPos, wireColor, wireId, sourceCommandType }) {
  if (!scene || !Array.isArray(sortedFloors) || !sortedFloors.length || !sourceFloorPlanId) return null;
  // Sadece üst/alt geçiş kat değiştirir (left/right aynı katta yön gösterir)
  if (direction !== 'up' && direction !== 'down') return null;

  const idx = sortedFloors.findIndex((f) => f.id === sourceFloorPlanId);
  if (idx < 0) return null;
  const target = direction === 'up' ? sortedFloors[idx + 1] : sortedFloors[idx - 1];
  if (!target) return null; // o yönde kat yok

  const currentFpMesh = findFloorPlanMeshById(scene, sourceFloorPlanId);
  const targetFpMesh = findFloorPlanMeshById(scene, target.id);
  if (!currentFpMesh || !targetFpMesh) return null;

  // Kolon-referanslı projeksiyon (kablo bacası ile AYNI yöntem): çizimin mesh/frame
  // içindeki ötelenmesini telafi ederek hedef katta karşılık gelen world noktayı bulur.
  // Kolon/merdiven bulunamazsa eski pür mesh-local yöntemine düş (graceful fallback).
  const projWorld = projectPointAcrossFloorMeshes(currentFpMesh, targetFpMesh, { x: worldPos.x, y: worldPos.y });
  const local = projWorld
    ? worldToMeshLocal(targetFpMesh, projWorld)
    : worldToMeshLocal(currentFpMesh, { x: worldPos.x, y: worldPos.y, z: 0 });

  // up → hedef ÜST kat → orada "alttan gelen"; down → hedef ALT kat → "üstten gelen"
  const incomingDirection = direction === 'up' ? 'fromBelow' : 'fromAbove';
  const marker = createIncomingArrow(null, {
    position: { x: local.x, y: local.y },
    direction: incomingDirection,
    color: wireColor || 0x0045ff,
    isPreview: false,
  });
  marker.userData = {
    ...marker.userData,
    type: 'incomingFloorTransitionArrow',
    direction: incomingDirection,
    sourceFloorPlanId,
    targetFloorPlanId: target.id,
    floorPlanId: target.id,
    sourceWireId: wireId || null,
    sourceCommandType: sourceCommandType || null, // tıklayınca devam edilecek komut
    isSelectable: true,
    _v: 3, // fpMesh-local koordinat (persist/rebuild için)
  };
  targetFpMesh.add(marker);
  return { marker, targetFloorId: target.id };
}

/**
 * Kalıcı floorTransitionArrow oluştur (Enter'da preview yerine).
 */
export function createPermanentFloorArrow({ scene, position, rotation, wireColor }) {
  const arrow = createArrow(scene, {
    position: { x: position.x, y: position.y },
    rotation,
    color: wireColor || 0x0045ff,
    layerName: '0',
    isPreview: false,
  });
  arrow.userData = {
    type: 'floorTransitionArrow',
    direction: directionFromRotation(rotation),
    rotation,
  };
  return arrow;
}
